import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory, useParams } from 'react-router-dom';
import { useRootStore } from 'store/rootStore';
import EnrollmentDynamicForm from 'components/common/form/EnrollmentDynamicForm';
import { runInAction, toJS } from 'mobx';
import { WEB_FORM_CUSTOM_COMPONENTS } from 'components/common/form/custom/FormCustomComponents';
import IncorporationDate from 'components/web-form/custom-components/SunLife/IncorporationDate';
import { FormStatuses } from 'types/formService';
import DDARAdditionalContactsInformation from 'components/common/form/custom/carrier-components/DDAR/AdditionalContactsInformation';
import { createStyles } from './styles';

interface Props {
  type?: 'RENEWAL';
}

const Form = observer((props: Props): JSX.Element => {
  // Common data
  const { periodId, formInstanceId } = useParams<{ periodId: string; formInstanceId: string }>();
  const history = useHistory();
  const classes = createStyles();
  // Mobx store
  const {
    webFormStore: {
      formInstance,
      dynamicForm,
      loading,
      effectiveDate,
      getFormInstance,
      setAnswers,
      ddarAdditionalContactsInformationDelegate,
    },
  } = useRootStore();

  const formTitle = formInstance?.form.formTitle;
  const convertedFormInstance = toJS(formInstance);
  const isFormCompleted = convertedFormInstance?.status === FormStatuses.COMPLETE;

  const redirectLink = props.type === 'RENEWAL' ? 'renewal-forms' : 'forms';

  // React effects
  useEffect(() => {
    void getFormInstance(formInstanceId, periodId);
  }, []);

  // Component actions
  async function onSaveAndExit(): Promise<void> {
    if (!isFormCompleted) {
      await setAnswers(false);
    }
    runInAction(() => {
      history.push(`/${redirectLink}/${periodId}`);
    });
  }

  async function onCompleteForm(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (dynamicForm?.isFormValid()) {
      await setAnswers(true);
      runInAction(() => {
        history.push(`/${redirectLink}/${periodId}`);
      });
    }
  }

  return (
    <>
      <div className={classes.formTitleWrapper}>
        <h1 className={classes.formTitle}>{formTitle}</h1>
      </div>
      <EnrollmentDynamicForm
        onCompleteForm={onCompleteForm}
        onSaveAndExit={onSaveAndExit}
        formInstance={formInstance}
        dynamicForm={dynamicForm}
        loading={loading}
        backBtnText={isFormCompleted ? 'Exit' : 'Save & Exit'}
        completeBtnText={isFormCompleted ? 'Update Form' : 'Complete Form'}
        periodId={periodId}
        customComponents={{
          ...WEB_FORM_CUSTOM_COMPONENTS,
          incorporationDate: (
            <IncorporationDate
              key="incorporationDate"
              effectiveDate={effectiveDate}
              field={dynamicForm?.customFields.incorporationDate}
            />
          ),
          ddarAdditionalContactsInformation: (
            <DDARAdditionalContactsInformation
              key="ddarAdditionalContactsInformation"
              delegate={ddarAdditionalContactsInformationDelegate}
            />
          ),
        }}
      />
    </>
  );
});

export default Form;
